import { esCodigoConocido } from '@discopico/domain';

/**
 * Lo compartido entre las capas de llamadas de cada funcionalidad.
 *
 * Existe porque `funcionalidades/autenticacion/api.ts` y `funcionalidades/usuarios/api.ts`
 * escribían las mismas tres funciones, y la tercera copia es donde una regla empieza a
 * divergir sin que nadie lo note. Las dos que importan acá ya se equivocaron una vez cada
 * una en el plan, así que conviene que haya un solo lugar donde estén bien.
 */

/**
 * Las variables se leen DENTRO de una función y no en el cuerpo del módulo.
 *
 * Es la adjudicación 38: un módulo con efecto al importarlo no se puede importar desde una
 * prueba. Y de paso, con la lectura en el cuerpo, la URL quedaba armada como
 * `undefined/functions/v1` cuando la variable falta, así que el fallo llegaba disfrazado
 * de error de red en vez de decir cuál variable no está definida.
 *
 * El nombre va escrito entero, nunca `import.meta.env[nombre]`: Vite solo reemplaza la
 * primera forma al compilar.
 */
function requerido(valor: string | undefined, nombre: string): string {
  if (!valor) throw new Error(`Falta la variable de entorno ${nombre}`);
  return valor;
}

export function urlFunciones(): string {
  return `${requerido(import.meta.env.VITE_SUPABASE_URL as string | undefined, 'VITE_SUPABASE_URL')}/functions/v1`;
}

/**
 * La base de storage, para el bucket privado de soportes de la fase 7.
 *
 * Va acá y no en un módulo propio por lo que dice el encabezado: la variable de entorno ya
 * se lee en dos lugares y la tercera copia es donde empieza a divergir. Y va por `fetch` y
 * no por `@supabase/storage-js`: el cliente de este proyecto se armó a mano justamente para
 * no cargar ese paquete, que pesa 5,73 KB comprimidos, y lo único que hace falta son dos
 * llamadas HTTP. La razón completa está en el encabezado de `cliente.ts`.
 */
export function urlStorage(): string {
  return `${requerido(import.meta.env.VITE_SUPABASE_URL as string | undefined, 'VITE_SUPABASE_URL')}/storage/v1`;
}

export function llavePublica(): string {
  return requerido(
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined,
    'VITE_SUPABASE_PUBLISHABLE_KEY',
  );
}

/**
 * Extrae el código del contrato de lo que devuelva Postgres, que es
 * `E_ALGO: detalle en prosa` cuando el `raise` lo escribió este proyecto y cualquier otra
 * cosa cuando no.
 *
 * **Si el primer trozo no es un código conocido, no se propaga.** Es la lección de la
 * tarea 14: partir por dos puntos y confiar en el resultado deja que un
 * `permission denied for table usuario` termine en la pantalla del operario. El mensaje
 * crudo va a la consola, que es donde sirve.
 */
export function codigoDe(mensaje: string): string {
  const primero = mensaje.split(':', 1)[0]?.trim() ?? '';
  if (esCodigoConocido(primero)) return primero;
  console.error('Error sin código del contrato:', mensaje);
  return 'E_DESCONOCIDO';
}
